import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { JiraSprintLiteData } from '../../../../api/endpoints/jira';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { Input, Menu } from '../../../../ui';
import { Checkbox } from '../../../../ui/Checkbox';
import { FixedScroller } from '../../../../ui/Scrollers';
import { useResolve } from '../../../Container';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { jiraCardSetCustom } from '../AtlassianCommands';
import { JiraPopperActionMenu } from '../fields/JiraPopperActionMenu';
import { JiraCard } from '../JiraCard';
import { useSelectedItemsIfApplicable } from './JiraMenuHooks';

const useJiraProjectSprintMenuQuery = (jiraProjectId: string, search: string, includeEmpty: boolean) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['jiraSprintMenu', jiraProjectId, search],
        queryFn: async () => {
            const base = includeEmpty && !search ? [{ id: null, name: 'No Sprint' }] : [];
            if (!!search) {
                // Yeah really
                const results = (await api.exec(v2.jira.fields.suggest('cf[10020]', search)))
                    .map(x => ({ id: x.value, name: x.label } as JiraSprintLiteData));
                return [...base, ...results];
            }
            else {
                const results = await api.exec(v2.jira.projects.sprints.list(jiraProjectId));
                return [...base, ...results.map(x => ({ id: x.id, name: x.name } as JiraSprintLiteData))];
            }
        },
    });
};

export type JiraSprintMenuProps = {
    jiraProjectId: string,
    includeEmpty?: boolean,
    selected?: JiraSprintLiteData[],
    onSelect(sprint: JiraSprintLiteData): void,
};

export const JiraSprintMenu = ({ jiraProjectId, includeEmpty, selected, onSelect }: JiraSprintMenuProps) => {

    const [filter, setFilter] = useState('');
    const { isLoading, data } = useJiraProjectSprintMenuQuery(jiraProjectId, filter, includeEmpty);
    const [dataWithSelected, selectedKey] = useSelectedItemsIfApplicable(data, selected?.map(x => x.id));

    const idKey = 'id';
    const iconKey = null;
    const labelKey = 'name';
    const onClick = onSelect;

    const onApply = () => {
        // if (filteredData?.length > 0) {
        //     onClick(filteredData[0]);
        // }
    };

    return <Flexer width="20">
        <Flexer padding=".5 .5 0 .5">
            <Input type="text" placeholder="Search" value={filter} onChange={setFilter} onApply={onApply} autoFocus compact />
        </Flexer>
        <JiraPopperActionMenu loading={isLoading}>
            <FixedScroller maxHeight="12">
                {dataWithSelected?.map(tm =>
                    <Menu.Item key={tm[idKey]} role="menuitem" aria-label={tm[labelKey] || ''} onClick={() => onClick(tm)}>
                        {!!iconKey && <img style={{ width: '1.5rem' }} src={tm[iconKey]} />}
                        <Menu.ItemTextCropped>
                            {tm[labelKey]}
                        </Menu.ItemTextCropped>
                        {!!selectedKey &&
                            <Menu.ItemPush>
                                <Checkbox.Input type="checkbox" checked={!!tm[selectedKey]} onChange={() => { }} />
                            </Menu.ItemPush>
                        }
                    </Menu.Item>
                )}
                {data?.length === 0 && <Menu.Item>
                    <Menu.Item>
                        <Menu.ItemTextCropped>
                            No matches found.
                        </Menu.ItemTextCropped>
                    </Menu.Item>
                </Menu.Item>}
            </FixedScroller>
        </JiraPopperActionMenu>
    </Flexer>;
};

export const SetJiraSprintForCardsMenu = ({ cards, onClose }: { cards: JiraCard[], onClose(): void }) => {

    const dispatch = useResolve(CommandDispatcher);

    const onSelect = (sprint: JiraSprintLiteData) => {
        // Special hack, if the null item, set the label to empty
        if (sprint.id === null) {
            sprint = { id: null, name: '' };
        }
        const first = cards[0];
        if (!first) return;
        const cf = first.customs.find(x => x.type === 'sprint');
        if (!cf) return;
        dispatch(jiraCardSetCustom, [cf.key, sprint, cards.map(x => x.id)]);
        onClose();
    };

    const jiraProjectId = cards[0]?.project;
    if (!jiraProjectId) return null;

    return <JiraSprintMenu jiraProjectId={jiraProjectId} onSelect={onSelect} includeEmpty={true} />;
};
